import { useLayoutEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import routesEn from "../locales/en/routes.json";
import routesFr from "../locales/fr/routes.json";
import tournamentsEn from "../locales/en/tournaments.json";
import tournamentsFr from "../locales/fr/tournaments.json";
import highHandsEn from "../locales/en/highHands.json";
import highHandsFr from "../locales/fr/highHands.json";
import { useTranslation } from "react-i18next";
import {
  matchHighHandPromotionPath,
  resolvePayloadHighHandLanguagePath,
} from "src/utils/highHandUtils";

// Utility to normalize paths (removes trailing slashes)
const normalizePath = (path) => path.replace(/\/+$/, "");

export const useLanguageChanger = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const routesConfig = {
    en: routesEn.routes,
    fr: routesFr.routes,
  };
  const tournamentsConfig = {
    en: tournamentsEn.tournaments,
    fr: tournamentsFr.tournaments,
  };
  const highHandConfig = {
    en: highHandsEn.highHands,
    fr: highHandsFr.highHands,
  };

  // Main function to change language and update the URL accordingly
  const changeLanguage = async (nextLanguage) => {
    const currentPathname = normalizePath(window.location.pathname);

    // Extract the current language from the pathname
    const matchLang = currentPathname.match(/^\/(fr|en)/);
    const currentLanguage = matchLang ? matchLang[1] : i18n.language;

    // Special handling for tournament URLs - simplified approach
    const tournamentPattern = /^\/(?:fr\/tournois|poker\/tournament)\/([A-Za-z0-9+/=]+)$/;
    let tournamentMatch = currentPathname.match(tournamentPattern);

    if (tournamentMatch) {
      // Tournament URL
      const tournamentId = tournamentMatch[1];

      // Simple language switch for tournament URLs
      const translatedUrl = nextLanguage === 'fr'
        ? `/fr/tournoi/${tournamentId}`
        : `/poker/tournament/${tournamentId}`;

      navigate(translatedUrl, { replace: true });
      i18n.changeLanguage(nextLanguage);
      localStorage.setItem("language", nextLanguage);
      return;
    }

    // 1. Try to find a static route in routesConfig
    const staticRouteKey = Object.keys(routesConfig[currentLanguage]).find(
      (key) =>
        normalizePath(routesConfig[currentLanguage][key]) === currentPathname
    );

    if (staticRouteKey && routesConfig[nextLanguage][staticRouteKey]) {
      navigate(routesConfig[nextLanguage][staticRouteKey], { replace: true });
    } else {
      // 2. Check tournamentsConfig
      const tournamentKey = Object.keys(tournamentsConfig[currentLanguage]).find(
        (key) =>
          Object.values(tournamentsConfig[currentLanguage][key])
            .map(normalizePath)
            .includes(currentPathname)
      );

      // 3. Check highHandConfig (static locale JSON map)
      const highHandKey = Object.keys(highHandConfig[currentLanguage]).find(
        (key) =>
          normalizePath(highHandConfig[currentLanguage][key]) === currentPathname
      );

      if (tournamentKey) {
        const tournamentRouteKey = Object.keys(
          tournamentsConfig[currentLanguage][tournamentKey]
        ).find(
          (key) =>
            normalizePath(
              tournamentsConfig[currentLanguage][tournamentKey][key]
            ) === currentPathname
        );

        if (
          tournamentRouteKey &&
          tournamentsConfig[nextLanguage][tournamentKey]
        ) {
          const translatedRoute =
            tournamentsConfig[nextLanguage][tournamentKey][tournamentRouteKey];
          navigate(translatedRoute, { replace: true });
        } else {
          console.error("No matching tournament route found for translation.");
          return;
        }
      } else if (highHandKey) {
        if (highHandConfig[nextLanguage][highHandKey]) {
          const translatedRoute = highHandConfig[nextLanguage][highHandKey];
          navigate(translatedRoute, { replace: true });
        } else {
          console.error("No matching high hand route found for translation.");
          return;
        }
      } else if (matchHighHandPromotionPath(currentPathname)) {
        // 3b. Payload-backed High Hand pages (localized slug not in highHands.json)
        const payloadPath = await resolvePayloadHighHandLanguagePath(
          currentPathname,
          nextLanguage
        );
        if (payloadPath) {
          navigate(payloadPath, { replace: true });
        } else {
          console.error("No matching high hand route found for translation.");
          return;
        }
      } else {
        // 4. Check for dynamic routes
        const dynamicRouteKey = Object.keys(routesConfig[currentLanguage]).find(
          (key) => {
            const pattern = `^${routesConfig[currentLanguage][key]
              .replace(/:[^\s/]+/g, "([^\\/]+)")
              .replace(/\//g, "\\/")}$`;
            return new RegExp(pattern).test(currentPathname);
          }
        );

        if (dynamicRouteKey && routesConfig[nextLanguage][dynamicRouteKey]) {
          const pattern = routesConfig[currentLanguage][dynamicRouteKey]
            .replace(/:[^\s/]+/g, "([^\\/]+)")
            .replace(/\//g, "\\/");
          const dynamicParts = currentPathname.match(new RegExp(`^${pattern}$`));

          if (dynamicParts) {
            dynamicParts.shift(); // Remove the full match
            let translatedRoute = routesConfig[nextLanguage][dynamicRouteKey];
            let paramIndex = 0;
            translatedRoute = translatedRoute.replace(/:[^\s/]+/g, () => {
              return dynamicParts[paramIndex++] || "";
            });
            navigate(translatedRoute, { replace: true });
          } else {
            console.error(
              "No matching static or dynamic route found for translation."
            );
            return;
          }
        } else {
          console.error(
            "No matching static or dynamic route found for translation."
          );
          return;
        }
      }
    }

    // Change language with i18next
    i18n.changeLanguage(nextLanguage);
    localStorage.setItem("language", nextLanguage);
  };

  // Automatically detect changes in the URL and force a language change if needed
  useLayoutEffect(() => {
    const currentPathname = window.location.pathname;
    const matchLang = currentPathname.match(/^\/(fr|)/);
    const currentLanguageFromUrl =
      matchLang && matchLang[1] === "fr" ? "fr" : "en";

    if (i18n.language !== currentLanguageFromUrl) {
      i18n.changeLanguage(currentLanguageFromUrl);
    }
  }, [i18n.language, location.pathname]);

  return changeLanguage;
};
